<template>
  <Form>
    <template #header>
      {{ header }}
    </template>

    <div>
      <div class="cm-paragraph">
        <AuInput
          v-model="name"
          :label="$t('companies.form.nameLabel')"
          required
          :placeholder="$t('companies.form.namePlaceholder')"
        />
      </div>
      <div class="cm-paragraph">
        <AuInput
          v-model="description"
          :label="$t('companies.form.descriptionLabel')"
          :placeholder="$t('companies.form.descriptionPlaceholder')"
          is-text-area
        />
      </div>
      <div class="cm-paragraph">
        <AuInput
          v-model="address"
          :label="$t('companies.form.addressLabel')"
          required
          :placeholder="$t('companies.form.addressPlaceholder')"
        />
      </div>
      <div class="cm-section">
        {{ $t('companies.form.companyInfoLabel') }}
      </div>
      <div class="d-flex cm-paragraph">
        <div class="cm-input cm-indent">
          <AuSelect
            :value="countryCode"
            :options="countryOptions"
            :placeholder="$t('companies.form.countryPlaceholder') + '(*)'"
            required
            @input="value => countryCode = value"
          />
        </div>
        <div class="cm-input">
          <AuInput
            v-model="url"
            :placeholder="$t('companies.form.URLPlaceholder')"
          />
        </div>
      </div>
      <div class="cm-section">
        {{ $t('companies.form.companyReqLabel') }}
      </div>
      <div class="d-flex cm-paragraph">
        <div class="cm-input cm-indent">
          <div class="cm-newline">
            <AuInput
              v-model="rusInn"
              :placeholder="$t('companies.form.INNPlaceholder')"
            />
          </div>
        </div>
        <div class="cm-input">
          <div class="cm-newline">
            <AuInput
              v-model="rusOgrn"
              :placeholder="$t('companies.form.OGRNPlaceholder')"
            />
          </div>
        </div>
      </div>
      <div class="cm-section">
        {{ $t("companies.form.logo") }}
      </div>
      <AuUpload
        :src="logoFile"
        class="cm-paragraph"
        @change="logoChange"
      />
    </div>
    <div
      v-if="isError"
      class="error"
    >
      {{ errorMsg }}
    </div>

    <template
      v-if="!loading"
      #footer
    >
      <AuButton
        bordered
        center
        type="primary"
        @click="addOrEdit"
      >
        {{ button }}
      </AuButton>
    </template>
  </Form>
</template>

<script>
import _ from "lodash";
import { mapActions, mapGetters } from "vuex";

export default {
  name: "CompanyManager",
  props: {
    id: {
      type: String,
      default: null
    }
  },

  data() {
    return {
      name: null,
      description: null,
      address: null,
      countryCode: null,
      url: null,
      isVerified: false,
      rusInn: null,
      rusOgrn: null,
      index: null,
      isError: null,
      errorMsg: null,
      logoFile: null,
      image: null
    };
  },

  computed: {
    ...mapGetters({
      country: "country",
      companies: "companies",
      companyCount: "companyCount"
    }),

    header() {
      return _.isEmpty(this.id) ?
        this.$t("companies.form.addCompanyLabel") :
        this.$t("companies.form.editCompanyLabel");
    },

    button() {
      return this.$t("companies.form.saveAsDraft");
    },

    countryOptions() {
      return _.map(this.country, c => ({ display: c.name, value: c.code }));
    },

    requiredFields() {
      return [
        { label: this.$t("companies.form.nameLabel"), value: this.name },
        { label: this.$t("companies.form.addressLabel"), value: this.address },
        { label: this.$t("companies.form.companyInfoLabel"), value: this.countryCode }
      ];
    }
  },
  async mounted() {
    if (_.isEmpty(this.country)) {
      await this.getCountry();
    }
    if (_.isEmpty(this.companies)) {
      await this.getCompanies();
    }
    if (!_.isEmpty(this.id)) {
      await this.setFields(this.companies.find((item, index) => {
        if (item.id == this.id) {
          this.index = index;
          return true;
        }
      }));
    }
  },

  methods: {
    ...mapActions({
      getCountry: "getCountry",
      getCompanies: "getCompanies",
      addCompany: "addCompany",
      editCompany: "editCompany",
      uploadFile: "files/uploadFile"
    }),

    goBack() {
      this.$router.push({ name: "companies" });
    },

    logoChange(data) {
      this.logoFile = data.src;
      this.image = data.image;
    },

    addOrEdit() {
      if (_.isEmpty(this.id)) {
        this.add();
      }
      else {
        this.edit();
      }
    },

    checkRequiredFields() {
      const res = _.find(this.requiredFields, field => !field.value || field.value === "");
      this.errorMsg = res ? this.$t("projects.form.error") + ` ${res.label}` : null;
      return res === undefined;
    },

    async add() {
      if (!this.checkRequiredFields()) {
        this.isError = true;
        return;
      }
      const params = {
        nameRus: this.name,
        nameEng: this.name,
        descriptionRus: this.description,
        descriptionEng: this.description,
        addressRus: this.address,
        addressEng: this.address,
        countryCode: this.countryCode,
        url: this.url,
        rusInn: this.rusInn,
        rusOgrn: this.rusOgrn,
        index: null
      };
      try {
        const data = await this.addCompany(params);
        if (this.image) {
          const upload = await this.uploadFile(this.image);
          await this.editCompany([data.id, { logoFile: `/api/uploaded_files/${upload.id}` }]);
          data.logoFile = upload;
        }
        this.companies.push(data);
        this.$store.commit("setCompanyCount", this.companyCount + 1);
        this.showSuccess(this.$t("toasts.success.companyAdd"));
        this.goBack();
      }
      catch (e) {
        this.showError(this.$t("toasts.error.companyAdd"));
      }
    },

    async setFields(data) {
      this.name = data.nameEng;
      this.description = data.descriptionEng;
      this.address = data.addressEng;
      this.countryCode = data.countryCode;
      this.url = data.url;
      this.isVerified = data.isVerified;
      this.rusInn = data.rusInn;
      this.rusOgrn = data.rusOgrn;
      this.logoFile = data?.logoFile?.name ?? "";
    },

    async edit() {
      if (!this.checkRequiredFields()) {
        this.isError = true;
        return;
      }
      const params = {
        nameRus: this.name,
        nameEng: this.name,
        descriptionRus: this.description,
        descriptionEng: this.description,
        addressRus: this.address,
        addressEng: this.address,
        countryCode: this.countryCode,
        url: this.url,
        rusInn: this.rusInn,
        rusOgrn: this.rusOgrn
      };
      try {
        const data = await this.editCompany([this.id, params]);
        if (this.image) {
          const upload = await this.uploadFile(this.image);
          await this.editCompany([data.id, { logoFile: `/api/uploaded_files/${upload.id}` }]);
          data.logoFile = upload;
        }
        this.companies[this.index] = data;
        this.showSuccess(this.$t("toasts.success.companyEdit"));
        this.goBack();
      }
      catch (e) {
        this.showError(this.$t("toasts.error.companyAdd"));
      }
    },

    showSuccess(message) {
      this.$toast.success(message);
    },

    showError(message) {
      this.$toast.error(message);
    }
  }
};
</script>

<style scoped lang="scss">
.cm-main {
  padding: 20px;
}
.cm-header{
  display: flex;
  justify-content: space-between;
  .title{
    font-weight: 500;
    font-size: 20px;
  }
}
.cm-section{
  font-size: 14px;
  margin-bottom: 6px;
  color: var(--color-50);
}
.cm-input{
  width: 50%;
}
.cm-indent{
  margin-right: 15px;
}
.cm-newline{
  margin-bottom: 10px;
}
.cm-paragraph{
  margin-bottom: 24px;
}
.error {
  display: flex;
  justify-content: center;
  margin-bottom: 10px;
  color: red;
}
</style>